// A boat can come in for service several times a season, and every visit is a vessel row of its own on the
// Service sheet: its works, dates, entry forms, photos and wash belong to that visit. A boat's visits are told
// by its SAP (the hull's number: a boat sold and renamed keeps it), else its name. They are put in time order
// (by season, then the visit's first date, then the sheet, where the newer visit sits higher) and numbered
// within their season, so the page can say "Visit 2 of 3" and list the boat's other visits.
import { compareSeasonIds } from './seasonTag.js';

/** a boat's identity across its visits: its SAP (a real number), else its name, spacing and case aside. */
export const boatIdentity = (v: { sap: string; name: string }): string => {
  const sap = String(v.sap ?? '').match(/\d{5,}/)?.[0];
  return sap ? `sap:${sap}` : `name:${v.name.trim().replace(/\s+/g, ' ').toLowerCase()}`;
};

/** what orders a visit: its season ('' when none), its first date ('' when none) and its place on the sheet. */
export interface VisitOrder { season: string; from: string; at: number }

/** older visit first. A visit with no season or no date counts as the newer (it was just registered); between
 *  two the dates can't tell apart, the one higher on the sheet is the newer. */
export function compareVisits(a: VisitOrder, b: VisitOrder): number {
  const last = (x: string, y: string, cmp: (p: string, q: string) => number) => (x === y ? 0 : !x ? 1 : !y ? -1 : cmp(x, y));
  return last(a.season, b.season, compareSeasonIds) || last(a.from, b.from, (p, q) => p.localeCompare(q)) || b.at - a.at;
}

export interface VisitInput { id: number; sap: string; name: string; season: string; dates: readonly string[] }

export interface ServiceVisit {
  id: number;
  name: string;
  season: string;
  /** its first and last dates ('' when it has none) */
  from: string;
  to: string;
  /** its number among the boat's visits of its season, and how many that season has */
  n: number;
  of: number;
}

/** each vessel's visit, with all of its boat's visits (oldest first), by row id. `rows` in sheet order. */
export function serviceVisits(rows: readonly VisitInput[]): Map<number, { visit: ServiceVisit; all: ServiceVisit[] }> {
  const byBoat = new Map<string, Array<ServiceVisit & VisitOrder>>();
  rows.forEach((r, at) => {
    const k = boatIdentity(r);
    if (k === 'name:') return;
    const list = byBoat.get(k) ?? byBoat.set(k, []).get(k)!;
    list.push({ id: r.id, name: r.name, season: r.season, from: r.dates[0] ?? '', to: r.dates[r.dates.length - 1] ?? '', n: 0, of: 0, at });
  });
  const out = new Map<number, { visit: ServiceVisit; all: ServiceVisit[] }>();
  for (const list of byBoat.values()) {
    list.sort(compareVisits);
    const perSeason = new Map<string, number>();
    for (const v of list) v.n = perSeason.set(v.season, (perSeason.get(v.season) ?? 0) + 1).get(v.season)!;
    for (const v of list) v.of = perSeason.get(v.season)!;
    const all: ServiceVisit[] = list.map(({ at: _at, ...v }) => v);
    for (const v of all) out.set(v.id, { visit: v, all });
  }
  return out;
}
